// NOTICE: This file was copied from the oppizi-shared repository, do
// not modify it here.

/**
 * @fileOverview
 * A chat message can be received that belongs to a conversation and user that haven't been stored
 * in the Redux state. While the UI flow guarantees that the conversation will eventually be
 * fetched, it does nothing of the sort to fill in the missing user data. This is what we call
 * "missing data".
 *
 * This file defines context-specific "detectors" (i.e. one detector just for user data), whose
 * roles are to check the Redux state for missing data and return a list of websocket requests that,
 * if made successfully, would fill in all of their gaps. Specifically, detectors return a list of
 * { key: <string>, fetchReq: <object> } where `key` is used to uniquely identify each `fetchReq` so
 * it's easy to tell if a particular request is new or not, and `fetchReq` is an object that's ready
 * to be sent through the websocket.
 *
 * Each time the Redux state changes, all detectors are executed and any newly detected missing data
 * is immediately requested. (Note that only the `websockets` slice of the Redux state is provided
 * to detectors.) In case of communication failure, all missing data is pooled and intermittently
 * requested.
 */

import { InvalidRequest } from '../error'

const RETRY_INTERVAL_MS = 10000

let configured = false

// --------- DETECTORS ---------------------------------------------------------
// If there is a new type of missing data you need to add, that'll be done here.
// You don't need to touch the fetchers afterwards - just follow by example and
// add the new detector function to `detectors`.

/**
 * Detects conversations with missing "participantsIds" data and returns fetch settings for them
 * @param {Object} state - Redux State
 * @returns {Array<Object>} - Array with fetchers
 */
const participantsIdsDetector = state =>
  Object.values(state.conversations)
    .filter(conversation => typeof conversation.participantsIds === 'undefined')
    .map(conversation => ({
      key: 'conversation' + conversation.id.toString(),
      fetchReq: {
        action: 'CHAT_GET_CONVERSATION',
        conversationId: conversation.id,
      },
    }))

/**
 * Detects users with missing data and returns fetch settings for them
 * @param {Object} state - Redux State
 * @returns {Array<Object>} - Array with fetchers
 */
const userDetector = state =>
  Object.values(state.users)
    .filter(_ => typeof _.firstName === 'undefined')
    .map(_ => ({
      key: 'user' + _.id.toString(),
      fetchReq: { action: 'CHAT_GET_USER', userId: _.id },
    }))

const detectors = [userDetector, participantsIdsDetector]

// ---------- FETCHER ----------------------------------------------------------
// This doesn't need to be changed if you want to add a new detector - see the
// DETECTORS section above.

let wasConnected
// Using an object instead of a list here for quick existence checks
let remainingFetchReqs = {}

const checkDetectors = (state, sendData) => {
  if (!state.connected) {
    wasConnected = false
    return
  }

  const allFetchReqs = Array.prototype.concat.apply(
    [],
    detectors.map(_ => _(state)),
  )

  const newFetchReqs = allFetchReqs
    .filter(_ => !(_.key in remainingFetchReqs))
    .map(_ => _.fetchReq)

  remainingFetchReqs = allFetchReqs.reduce((obj, _) => {
    obj[_.key] = _.fetchReq
    return obj
  }, {})

  fetchAll(
    wasConnected ? newFetchReqs : Object.values(remainingFetchReqs),
    sendData,
  )

  wasConnected = true
}

const fetchAll = (fetchReqs, sendData) => {
  for (const data of fetchReqs) sendData(data)
}

const configure = (store, sendData) => {
  if (configured) throw new InvalidRequest('Cannot configure more than once')
  configured = true

  store.subscribe(() => checkDetectors(store.getState().websockets, sendData))

  setInterval(() => {
    if (!store.getState().websockets.connected) return
    fetchAll(Object.values(remainingFetchReqs), sendData)
  }, RETRY_INTERVAL_MS)
}

export default configure
